import React, { useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { Box } from '@common-fe/common-fe';

import { FeatureFlagKeys, OrganizationTypes, PermissionKey } from '@/common/constants';
import Permissions from '@/common/permissions';
import ROUTES from '@/common/routes';
import { Access } from '@/modules/core/components';
import { useCurrentOrganization, } from '@/modules/core/hooks';
import useGetPermissionState from '@/modules/user/hooks/useGetPermissionState';
import useReversedFeatureFlag from '@/utils/hooks/useFeatureFlag';

import ExpressSetupLinks from './ExpressSetupLinks/ExpressSetupLinks';
import useGetExpressSetupLinksFieldsPermProps from './ExpressSetupLinks/hooks/useGetExpressSetupLinksFieldsPermProps';
import { PayrollCalendarSection } from './PayrollCalendar/PayrollCalendarSection';
import PayrollCalendarTable from './PayrollCalendar/PayrollCalendarTable';
import PayrollGroupModal from './PayrollGroups/PayrollGroupModal';
import PayrollGroupTable from './PayrollGroups/PayrollGroupTable';
import { CONFIRM_TEXT } from './PlanModal/PlanFormModal';
import useGetAllowRenewalsFieldsPermProps from './PlansRenewal/hooks/useGetAllowRenewalsFieldsPermProps';
import useGetReimbursementFieldsPermProps from './Reimbursement/hooks/useGetReimbursementFieldsPermProps';
import { AIAdjudication } from './AIAdjudication';
import OrphanConfiguration from './OrphanConfiguration';
import { PlansRenewal } from './PlansRenewal';
import PlanTemplates from './PlanTemplates';
import PlanYearWrapper from './PlanYearWrapper';
import { Reimbursement } from './Reimbursement';

interface Props {
  disabled?: boolean;
  payrollGroupsDisabled?: boolean;
  isSubsidiaryPage?: boolean;
}

export const Features: React.FC<Props> = ({ disabled, payrollGroupsDisabled, isSubsidiaryPage }) => {
  const { path } = useRouteMatch();
  const { isHide: isAiAdjudicationHide } = useGetPermissionState(PermissionKey.IS_AI_ADJUDICATION);
  const { isHide: isAiConfidenceLevelHide } = useGetPermissionState(PermissionKey.AI_CONFIDENCE_LEVEL);

  const isPartnerPage = useMemo(
    () => path === ROUTES.PARTNERS_EDIT() || path === ROUTES.PARTNERS_VIEW(),
    [path]
  );
  const isDistributorPage = useMemo(
    () => path === ROUTES.DISTRIBUTORS_EDIT() || path === ROUTES.DISTRIBUTORS_VIEW(),
    [path]
  );
  const {
    observingOrganization: { type },
  } = useCurrentOrganization();

  const isSystemLevel = useMemo(() => type === OrganizationTypes.system, [type]);
  const hasPlanFunctionality = useMemo(
    () => !isPartnerPage && !isDistributorPage,
    [isPartnerPage, isDistributorPage]
  );

  const hasAiAdjudication = useMemo(
    () => !(isAiAdjudicationHide && isAiConfidenceLevelHide),
    [isAiAdjudicationHide, isAiConfidenceLevelHide]
  );

  const { hasFields: hasExpressSetupLinksFields} = useGetExpressSetupLinksFieldsPermProps();
  const { hasFields: hasAllowRenewalsFields } = useGetAllowRenewalsFieldsPermProps();

  const isPayrollGroupsAvailable = useMemo(
    () => !isPartnerPage && !isDistributorPage && !isSystemLevel,
    [isPartnerPage, isDistributorPage, isSystemLevel, ]);

  const isPlansRenewalAvailable = useReversedFeatureFlag(FeatureFlagKeys.PLANS_RENEWAL, {
    dev: true,
    uat: true
  });

  const { hasFields: hasReimbursementFields } = useGetReimbursementFieldsPermProps();

  return (
    <Box direction="column" background="module">
      {isPartnerPage && (
        <Box
          background="canvas"
          round="container1Round"
          margin={{ bottom: 'spacing24' }}
          border={{ color: 'border2', size: 'small' }}
        >
          <PlanTemplates disabled={disabled} />
        </Box>
      )}
      {!isSystemLevel && hasPlanFunctionality && (
        <Box
          background="canvas"
          margin={{ bottom: 'spacing24' }}
          round="container1Round"
          id="plan_year_section"
          border={{ color: 'border2', size: 'small' }}
        >
          <PlanYearWrapper disabled={disabled} confirmText={CONFIRM_TEXT} isSubsidiaryPage={isSubsidiaryPage} />
        </Box>
      )}
      {isPayrollGroupsAvailable && (
        <Box
          id="payroll_group"
          background="canvas"
          round="container1Round"
          margin={{ top: 'l' }}
          border={{ color: 'border2', size: 'small' }}
        >
          <PayrollGroupModal disabled={payrollGroupsDisabled} />
          <Access
            accessRequirements={[{ permission: Permissions.VIEW_ER }, { permission: Permissions.EDIT_ER_DATA }]}
          >
            <PayrollGroupTable />
          </Access>
        </Box>
      )}
      {isPayrollGroupsAvailable && (
        <Box
          id="payroll_calendar"
          background="canvas"
          round="container1Round"
          margin={{ top: 'l' }}
          border={{ color: 'border2', size: 'small' }}
        >
          <PayrollCalendarSection disabled={payrollGroupsDisabled} />
          <Access
            accessRequirements={[{ permission: Permissions.VIEW_ER }, { permission: Permissions.EDIT_ER_DATA }]}
          >
            <PayrollCalendarTable />
          </Access>
        </Box>
      )}

      {hasReimbursementFields ? (
        <Box {...(hasPlanFunctionality ? { margin: { top: 'spacing24' } } : {})}>
          <Reimbursement disabled={disabled} />
        </Box>
      ) : null}

      {hasAiAdjudication && (
        <Box margin={{ top: 'spacing24' }} id="ai_adjudication">
          <AIAdjudication />
        </Box>
      )}
      <OrphanConfiguration />
      
      {hasExpressSetupLinksFields && (
        <Box margin={{ top: 'spacing24' }}>
          <ExpressSetupLinks isDistributorMode={isDistributorPage} />
        </Box>
      )}

      {isPlansRenewalAvailable && hasAllowRenewalsFields && !isSubsidiaryPage && (
        <Box margin={{ top: 'spacing24' }}>
          <PlansRenewal />
        </Box>
      )}
    </Box>
  );
};
